'use client'
import { createTheme, ThemeProvider, Box, GlobalStyles } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs from 'dayjs'

function TimeCustom({
	name,
	setDataFunction,
	data,
	value,
	required,
	disabled,
	data_cy,
	className,
	label = false,
	isRange = false,
	startName = 'startTime',
	endName = 'endTime',
	startLabel = 'Start Time',
	endLabel = 'End Time',
	...props
}) {
	const handleChange = (newValue) => {
		if (setDataFunction && data && name) {
			setDataFunction({
				...data,
				[name]: newValue,
			})
		}
	}

	const handleStartChange = (newValue) => {
		if (setDataFunction && data) {
			setDataFunction({
				...data,
				[startName]: newValue,
			})
		}
	}

	const handleEndChange = (newValue) => {
		if (setDataFunction && data) {
			setDataFunction({
				...data,
				[endName]: newValue,
			})
		}
	}

	// Unified theme (includes fix for MuiPickersSectionList-root)
	const customTheme = createTheme({
		zIndex: {
			modal: 9999,
		},
		components: {
			MuiPickersSectionList: {
				styleOverrides: {
					root: {
						padding: 0, // <- REMOVE inner padding
						margin: 0,
					},
				},
			},
			MuiPickerPopper: {
				styleOverrides: {
					root: {
						backgroundColor: 'transparent',
						boxShadow: 'none',
						backdropFilter: 'none',
						zIndex: 9999,
					},
				},
			},
			MuiPaper: {
				styleOverrides: {
					root: {
						'&.MuiPickersPopper-paper': {
							backgroundColor: '#fff',
							borderRadius: '8px',
							border: '1px solid var(--Stroke-Color, #E8EBEE)',
						},
					},
				},
			},
			MuiTimeClock: {
				styleOverrides: {
					root: {
						backgroundColor: '#fff',
						borderRadius: '16px',
						padding: 0,
					},
				},
			},
			MuiClockNumber: {
				styleOverrides: {
					root: {
						color: '#313131',
						fontWeight: 500,
						'&.Mui-selected': {
							backgroundColor: '#7CA6BD',
							color: '#fff',
						},
					},
				},
			},
			MuiClockPointer: {
				styleOverrides: {
					root: { backgroundColor: '#7CA6BD' },
					thumb: {
						backgroundColor: '#7CA6BD',
						border: '1px solid #7CA6BD',
					},
				},
			},
		},
	})

	// Input styling (same as DateCustom)
	const inputPropsStyles = {
		padding: '4px',
		borderRadius: '8px',
		border: '1px solid #E8EBEE',
		fontSize: '14px',
		background: '#fff',
		color: '#313131',
		width: '100%',
		'& fieldset': { border: 'none' },
	}

	const labelStyle = {
		font: 'var(--body-font-three)',
		marginBottom: '5px',
		fontWeight: '500',
		fontSize: '14px',
		color: 'var(--secondary-Text)',
		display: 'block',
	}

	// Reusable slotProps for all TimePickers
	const pickerSlotProps = (placeholder, cy) => ({
		textField: {
			placeholder,
			'data-cy': cy,
			fullWidth: true,
			InputProps: { sx: inputPropsStyles },
		},
		popper: {
			sx: { zIndex: '9999 !important' },
		},
	})

	return (
		<ThemeProvider theme={customTheme}>
			<GlobalStyles
				styles={{
					'.MuiPickerPopper-root': {
						zIndex: '9999 !important',
					},
				}}
			/>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				{isRange ? (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 2,
							width: '100%',
						}}
					>
						{/* Start Time */}
						<Box sx={{ width: '100%' }}>
							{label && (
								<label className={className} style={labelStyle}>
									{startLabel}
									{required && (
										<span style={{ color: 'red' }}> *</span>
									)}
								</label>
							)}

							<TimePicker
								value={
									data?.[startName]
										? dayjs(data[startName])
										: null
								}
								onChange={handleStartChange}
								disabled={disabled}
								slotProps={pickerSlotProps(
									'Select start time',
									data_cy ? `${data_cy}-start` : undefined
								)}
								{...props}
								sx={{ width: '100%' }} // FORCE full width
							/>
						</Box>

						{/* End Time */}
						<Box sx={{ width: '100%' }}>
							{label && (
								<label className={className} style={labelStyle}>
									{endLabel}
									{required && (
										<span style={{ color: 'red' }}> *</span>
									)}
								</label>
							)}

							<TimePicker
								value={
									data?.[endName]
										? dayjs(data[endName])
										: null
								}
								onChange={handleEndChange}
								disabled={disabled}
								slotProps={pickerSlotProps(
									'Select end time',
									data_cy ? `${data_cy}-end` : undefined
								)}
								{...props}
								sx={{
									width: '99%',
									'& .MuiPickersInputBase-root': {
										padding: '8px !important',
									},
									'& .MuiPickersSectionList-root': {
										padding: '0 15px !important',
									},
								}}
							/>
						</Box>
					</Box>
				) : (
					<>
						{label && (
							<label className={className} style={labelStyle}>
								{label}
								{required && (
									<span style={{ color: 'red' }}> *</span>
								)}
							</label>
						)}

						<TimePicker
							value={value ? dayjs(value) : null}
							onChange={handleChange}
							disabled={disabled}
							slotProps={pickerSlotProps('Select time', data_cy)}
							sx={{
								width: '99%',
								'& .MuiPickersInputBase-root': {
									padding: '8px !important',
								},
								'& .MuiPickersSectionList-root': {
									padding: '0 15px !important',
								},
							}}
							{...props}
						/>
					</>
				)}
			</LocalizationProvider>
		</ThemeProvider>
	)
}

export default TimeCustom
